import {
  Button,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Page,
} from '@dixa/lib-shared-habla';
import {
  ApiErrorStatusCode,
  isQueryError,
  isResponseWithData,
} from 'lib-shared-auth';
import { useForm } from 'react-hook-form';

import { trustpilotApi } from 'apis/trustpilot/trustpilot.api';
import { AppLink } from 'components/appLink';
import { HookFormQueuesSelect, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { TrustpilotScorePicker } from '../../components/trustpilotScorePicker/trustpilotScorePicker';

const formId = 'settings-reviews-create-form';

type SettingsReviewsFormValues = {
  identifying_domain: string;
  default_queue_id: string[];
  score_filter?: number;
};

const defaultValues: SettingsReviewsFormValues = {
  identifying_domain: '',
  default_queue_id: [],
};

const isResponseWithErrors = (
  response: unknown,
): response is { errors: string[] } =>
  Array.isArray((response as { errors?: string[] })?.errors);

export const SettingsReviewsCreateView = (): JSX.Element => {
  const notify = useNotifications();
  const navigate = useAppNavigate();

  const [createTrustpilotIntegration] =
    trustpilotApi.useCreateTrustpilotIntegrationMutation();

  const form = useForm<SettingsReviewsFormValues>({
    mode: 'all',
    defaultValues,
  });

  const scoreFilter = form.watch('score_filter');

  const handleSubmit = async ({
    default_queue_id,
    identifying_domain,
    score_filter,
  }: SettingsReviewsFormValues) => {
    const response = await createTrustpilotIntegration({
      identifying_domain,
      default_queue_id: default_queue_id.at(0),
      score_filter: score_filter ? { max: score_filter } : undefined,
    });
    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Trustpilot review created',
          icon: 'i-done',
          time: 3000,
        },
        NotificationType.Success,
      );

      navigate({
        path: '/settings/reviews/:id',
        params: { path: { id: response.data.data.id } },
      });
    } else {
      const queryError = isQueryError(response.error) ? response.error : null;
      const responseErrors =
        queryError && isResponseWithErrors(queryError.data)
          ? queryError.data.errors
          : [];

      if (queryError?.status === ApiErrorStatusCode.Conflict) {
        form.setError('identifying_domain', {
          message: 'Domain is unauthorized or not valid',
        });
      }

      notify(
        {
          title:
            responseErrors[0] || 'Error while creating the Trustpilot review.',
          text: responseErrors[1] || '',
        },
        NotificationType.Error,
      );
    }
  };

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path="/settings/reviews" style={{ color: 'inherit' }}>
              Reviews
            </AppLink>
            <Icon name="angle-right" />
            Create review domain
          </FlexBox>
        }
      />
      <Page.Body>
        <Page.Section
          title="Create review domain"
          controls={
            <Button
              type="submit"
              isLoading={form.formState.isSubmitting}
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                form.formState.isSubmitting
              }
              form={formId}
            >
              Create review domain
            </Button>
          }
        >
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormGrid>
              <FormField span={4}>
                <HookFormText
                  name="identifying_domain"
                  label="Identifying domain"
                  control={form.control}
                  rules={{
                    required: 'Identifying domain is required',
                    validate: {
                      nonBlank: (value) =>
                        !value.trim()
                          ? 'Identifying domain should not be blank'
                          : undefined,
                    },
                  }}
                  componentProps={{
                    placeholder: 'Write your identifying domain name',
                  }}
                />
              </FormField>

              <FormField span={4}>
                <HookFormQueuesSelect
                  name="default_queue_id"
                  label="Default queue for incoming reviews"
                  control={form.control}
                  rules={{
                    required: 'Queue is required',
                  }}
                />
              </FormField>
              <TrustpilotScorePicker
                selected={scoreFilter}
                onSelect={(value) => {
                  form.setValue('score_filter', value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
              />
            </FormGrid>
          </form>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
